import { type ButtonHTMLAttributes, type Ref, useId } from "react";
import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { AdjustmentsIcon } from "@icons";
import styles from "./ColumnSettings.module.scss";

interface ColumnSettingsTriggerProps extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "children"
> {
    /** The columns differ from their default, which puts a dot on the gear. */
    hasChanges: boolean;
    variant?: TriggerButtonProps["variant"];
    size?: TriggerButtonProps["size"];
    ref?: Ref<HTMLButtonElement>;
}

/**
 * The column gear: an icon-only button that opens a column settings panel.
 * Pass it as a `SettingsPanel.Root` trigger, which adds the popover props.
 * While the columns differ from their default it shows a corner dot and
 * describes the change to screen readers.
 */
export const ColumnSettingsTrigger = ({
    hasChanges,
    variant = "primary",
    size = "md",
    disabled,
    className,
    "aria-describedby": callerDescribedBy,
    ...props
}: ColumnSettingsTriggerProps) => {
    const changedDescriptionId = useId();
    const describedBy =
        [callerDescribedBy, hasChanges ? changedDescriptionId : undefined]
            .filter(Boolean)
            .join(" ") || undefined;

    return (
        <Button
            {...props}
            variant={variant}
            size={size}
            iconOnly
            aria-label="Configure columns"
            aria-describedby={describedBy}
            disabled={disabled}
            className={classNames(className, {
                [styles.columnsChanged]: hasChanges,
            })}
        >
            <AdjustmentsIcon
                className={classNames({ "icon-color-gray-900": !disabled })}
            />
            {hasChanges && (
                <span
                    id={changedDescriptionId}
                    className={styles.changedDescription}
                >
                    Columns changed from default
                </span>
            )}
        </Button>
    );
};
